import React, { useMemo } from 'react'
import { BlueText, Grid } from '../styles'
import { InputLabel } from 'components/InputLabel'
import { CustomDatePicker } from 'components/Datepicker'
import { InputSelect } from 'components/InputSelect'
import {
    variablesListPacientDocumentType,
    variablesListPacientGender
} from '../helper'
import { NotificationType } from 'types/notification'
import {
    Control,
    FieldErrors,
    FieldValues,
    UseFormRegister,
    UseFormSetValue,
    UseFormWatch
} from 'react-hook-form'
import { IVariableReducer } from 'types/variable'

interface InvolvesPatientProps {
    notification: NotificationType
    errors: FieldErrors<FieldValues>
    setValue: UseFormSetValue<FieldValues>
    control: Control<FieldValues, any>
    register: UseFormRegister<FieldValues>
    variableSelector: IVariableReducer
    watch: UseFormWatch<FieldValues>
    involvesPatient: any
    canEditStepOne: boolean
    statusIsArchivedOrFinished: boolean
    isProfileCorporateQualityBoard: boolean
    involvePatientMoreThenOne: boolean
}

export const InvolvesPatient = ({
    notification,
    errors,
    setValue,
    control,
    register,
    variableSelector,
    watch,
    involvesPatient,
    canEditStepOne,
    statusIsArchivedOrFinished,
    isProfileCorporateQualityBoard,
    involvePatientMoreThenOne
}: InvolvesPatientProps) => {
    if (!involvesPatient || (involvesPatient && involvePatientMoreThenOne)) {
        return null
    }
    const incidentDate = watch('date_incident') || ''

    // Verifica se o campo deve ser somente leitura baseado no perfil OU no status da notificação
    const isReadOnly = useMemo(() => {
        return isProfileCorporateQualityBoard || statusIsArchivedOrFinished
    }, [isProfileCorporateQualityBoard, statusIsArchivedOrFinished])

    const getMaxDate = () => {
        if (!incidentDate) {
            return new Date()
        }
        return new Date(incidentDate?.toISOString())
    }

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const result = event.target.value.replace(/[^a-z]/gi, '')
        setValue(
            'patient_initials',
            result.toLocaleUpperCase()?.split('').join('.')
        )
    }

    return (
        <div className="col-span-4 mb-[128px]">
            <BlueText>Dados do paciente</BlueText>
            <Grid className="xl:grid-cols-10 lg:grid-cols-5  grid-cols-1">
                <div className="xl:col-span-2 lg:col-span-1">
                    <InputLabel
                        error={
                            errors.patient_initials && {
                                message: 'Preenchimento obrigatório.'
                            }
                        }
                        className={
                            errors.patient_initials && 'border-errorText'
                        }
                        disabled={!involvesPatient}
                        label="Iniciais do nome"
                        defaultValue={
                            notification?.patient_initials?.display_value
                        }
                        name="patient_initials"
                        register={register}
                        maxLength={30}
                        onChange={handleChange}
                    />
                </div>

                <div className="xl:col-span-2 lg:col-span-1">
                    <CustomDatePicker
                        error={!!errors?.patient_date_of_birth}
                        errorText="Preenchimento obrigatório."
                        disabled={
                            !involvesPatient ||
                            statusIsArchivedOrFinished ||
                            !canEditStepOne
                        }
                        label="Data de nascimento"
                        name="patient_date_of_birth"
                        control={control}
                        required
                        placeholderData="Selecionar"
                        dateValue={new Date()}
                        maxDate={getMaxDate()}
                    />
                </div>

                <div className="xl:col-span-2 lg:col-span-1">
                    <InputSelect
                        placeholder="Selecionar"
                        errorStyle={`${
                            errors.patient_gender
                                ? 'border border-errorText'
                                : ''
                        }`}
                        styleBoxOptions="mt-3.5"
                        searchPlaceHolder="Buscar"
                        disabled={
                            !involvesPatient || isReadOnly
                        }
                        label="Gênero do paciente"
                        name="patient_gender"
                        control={control}
                        data={variablesListPacientGender(variableSelector)}
                        defaultValue={notification?.patient_gender?.value}
                    />
                </div>

                <div className="xl:col-span-2 lg:col-span-1">
                    <InputSelect
                        errorStyle={`${
                            errors.document_type
                                ? 'border border-errorText'
                                : ''
                        }`}
                        placeholder="Selecionar"
                        styleBoxOptions="mt-3.5"
                        searchPlaceHolder="Buscar"
                        disabled={!involvesPatient}
                        label="Tipo de documento"
                        name="document_type"
                        control={control}
                        data={variablesListPacientDocumentType(
                            variableSelector
                        )}
                        defaultValue={notification?.document_type?.value}
                    />
                </div>

                <div className="xl:col-span-2 lg:col-span-1">
                    <InputLabel
                        error={
                            errors.document_number && {
                                message: 'Preenchimento obrigatório.'
                            }
                        }
                        className={errors.document_number && 'border-errorText'}
                        disabled={!involvesPatient}
                        label="Número do documento"
                        defaultValue={
                            notification?.document_number?.display_value
                        }
                        name="document_number"
                        register={register}
                        maxLength={30}
                    />
                </div>
            </Grid>
        </div>
    )
}
